import { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Stack, Typography, Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import AttendanceSection from "../components/AttendanceSection";
import AttendanceExportPdfModal from "../components/AttendanceExportPdfModal";
import {
  getAttendanceDailyBoard,
  getAttendanceWeeklyTotals,
  getAttendanceBranches,
  getAttendanceAreas,
  exportAttendancePdf,
} from "../api/asistenciaApi";
import { useEmpresa } from "../../../../app/providers/EmpresaProvider";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function fmtYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function startOfWeekMonday(date) {
  const next = new Date(date);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;

  next.setDate(next.getDate() + diff);
  next.setHours(0, 0, 0, 0);

  return next;
}

function endOfWeekMonday(date) {
  return addDays(startOfWeekMonday(date), 6);
}

const MONTHS_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function formatWeekLabel(start, end) {
  if (start.getMonth() === end.getMonth()) {
    return `Semana del ${start.getDate()} al ${end.getDate()} de ${MONTHS_ES[start.getMonth()]}`;
  }

  return `Semana del ${start.getDate()} de ${MONTHS_ES[start.getMonth()]} al ${end.getDate()} de ${MONTHS_ES[end.getMonth()]}`;
}

function Pill({ children, sx }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.1,
        bgcolor: "secondary.50",
        borderRadius: "33px",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default function AsistenciaPage() {
  const navigate = useNavigate();
  const { empresaId, empresaNombre, sucursalId } = useEmpresa();

  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [dailyRows, setDailyRows] = useState([]);
  const [weeklyTotals, setWeeklyTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterBranchId, setFilterBranchId] = useState("");
  const [filterAreaId, setFilterAreaId] = useState("");
  const [search, setSearch] = useState("");
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const [branchesList, setBranchesList] = useState([]);
  const [areasList, setAreasList] = useState([]);

  const [toast, setToast] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  const weekStart = useMemo(() => startOfWeekMonday(selectedDate), [selectedDate]);
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, index) => fmtYMD(addDays(weekStart, index))),
    [weekStart]
  );
  const weekEnd = useMemo(() => endOfWeekMonday(selectedDate), [selectedDate]);
  const weekLabel = useMemo(
    () => formatWeekLabel(weekStart, weekEnd),
    [weekStart, weekEnd]
  );

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        const [branches, areas] = await Promise.all([
          getAttendanceBranches(
            { licenseId: empresaId || undefined },
            { signal: ac.signal }
          ),
          getAttendanceAreas(
            { licenseId: empresaId || undefined },
            { signal: ac.signal }
          ),
        ]);

        setBranchesList(Array.isArray(branches) ? branches : []);
        setAreasList(Array.isArray(areas) ? areas : []);
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error("attendance filters error:", err);
          setToast({
            open: true,
            severity: "error",
            message: "No se pudieron cargar los filtros de asistencia.",
          });
        }
      }
    })();

    return () => ac.abort();
  }, [empresaId]);

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setFetchError("");

        const [responses, totals] = await Promise.all([
          Promise.all(
            weekDates.map((date) =>
              getAttendanceDailyBoard(
                {
                  licenseId: empresaId || undefined,
                  branchId: filterBranchId || sucursalId || undefined,
                  search: search.trim() || undefined,
                  date,
                },
                { signal: ac.signal }
              )
            )
          ),
          getAttendanceWeeklyTotals(
            {
              licenseId: empresaId || undefined,
              branchId: filterBranchId || sucursalId || undefined,
              areaId: filterAreaId || undefined,
              search: search.trim() || undefined,
              fromDate: weekDates[0],
              toDate: weekDates[weekDates.length - 1],
            },
            { signal: ac.signal }
          ),
        ]);

        const rows = responses.flatMap((response) => (Array.isArray(response) ? response : []));

        setDailyRows(Array.isArray(rows) ? rows : []);
        setWeeklyTotals(totals || null);
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error("getAttendanceDailyBoard week error:", err);
          setFetchError("No se pudo cargar la asistencia de la semana.");
          setDailyRows([]);
          setWeeklyTotals(null);
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [empresaId, sucursalId, filterBranchId, filterAreaId, search, weekDates, refreshKey]);

  const handlePrevWeek = () => {
    setSelectedDate((date) => addDays(date, -7));
  };

  const handleNextWeek = () => {
    setSelectedDate((date) => addDays(date, 7));
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const handleAttendanceSaved = () => {
    setRefreshKey((value) => value + 1);
  };

  const handleDownloadAttendancePdf = async (payload) => {
    if (exportingPdf) return;

    try {
      setExportingPdf(true);

      await exportAttendancePdf({
        ...payload,
        licenseId: empresaId || undefined,
        licenseName: empresaNombre || undefined,
      });

      setToast({
        open: true,
        severity: "success",
        message: "PDF de asistencia descargado correctamente.",
      });
      setExportModalOpen(false);
    } catch (err) {
      console.error("export asistencia pdf error:", err);
      setToast({
        open: true,
        severity: "error",
        message: err?.message || "No se pudo descargar el PDF de asistencia.",
      });
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        pb: 2,
        overflowX: "hidden",
        height: "100%",
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <IconButton
          onClick={() => navigate("/rrhh/dashboard")}
          sx={{
            width: 36,
            height: 36,
            bgcolor: "secondary.500",
            "&:hover": { bgcolor: "secondary.600" },
          }}
        >
          <ArrowBackIcon sx={{ color: "logoblanco", fontSize: 32 }} />
        </IconButton>

        <Stack sx={{ flex: 1, alignItems: "center", pr: "48px" }}>
          <Pill sx={{ bgcolor: "secondary.200" }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: "primary.900",
              }}
            >
              Asistencia
            </Typography>
          </Pill>
        </Stack>
      </Stack>

      <AttendanceSection
        dailyRows={dailyRows}
        weeklyTotals={weeklyTotals}
        selectedDate={selectedDate}
        weekLabel={weekLabel}
        onPrevWeek={handlePrevWeek}
        onNextWeek={handleNextWeek}
        search={search}
        onSearch={setSearch}
        branchId={filterBranchId}
        onBranchId={setFilterBranchId}
        areaId={filterAreaId}
        onAreaId={setFilterAreaId}
        branches={branchesList}
        areas={areasList}
        loading={loading}
        fetchError={fetchError}
        onAttendanceSaved={handleAttendanceSaved}
        onOpenExportPdf={() => setExportModalOpen(true)}
        exportingPdf={exportingPdf}
      />

      <AttendanceExportPdfModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onDownload={handleDownloadAttendancePdf}
        downloading={exportingPdf}
        licenseId={empresaId || undefined}
        selectedDate={selectedDate}
        branches={branchesList}
        areas={areasList}
        initialBranchId={filterBranchId || sucursalId || ""}
        initialAreaId={filterAreaId}
        search={search}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeToast}
          severity={toast.severity}
          sx={{ width: "100%", borderRadius: "14px" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
